import { Dispatch } from '@reduxjs/toolkit'
import { AccountGroupType } from 'api/Trading-Accounts/Types/AccountGroupType'
import { CountryOverridesResponse } from 'api/Trading-Accounts/Types/CountryOverridesType'
import { PageQuery } from 'utils/api'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'

import { tradingAccountsSlice } from './trading-accounts.slice'

export const fetchTradingAccountRestrictions = (
  tradingAccountsApiClient: TradingAccountsApiClient
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const tradingAccountRestrictionsResponse =
        await tradingAccountsApiClient.getTradingAccountRestrictions()
      dispatch(tradingAccountsSlice.actions.setRestrictionTypes(tradingAccountRestrictionsResponse))
    } catch (e) {
      console.error('Error during get wallet restrictions', e)
    }
  }
}

export const fetchInstrumentsAction = (tradingAccountsApiClient: TradingAccountsApiClient) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getInstruments()
      dispatch(tradingAccountsSlice.actions.setInstruments(data))
    } catch (e) {
      console.error('Cannot get instruments.', e)
    }
  }
}

export const fetchTradingAccountsLeverageTypesAction = (
  tradingAccountsApiClient: TradingAccountsApiClient
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getTradingAccountLeverageType()
      dispatch(tradingAccountsSlice.actions.setLeverageTypes(data))
    } catch (e) {
      console.error('Cannot get client types', e)
    }
  }
}

export const fetchPlatformTypesAction = (tradingAccountsApiClient: TradingAccountsApiClient) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getTradingAccountPlatformType()
      dispatch(tradingAccountsSlice.actions.setPlatformTypes(data))
    } catch (e) {
      console.error('Cannot get platform types', e)
    }
  }
}

export const fetchBooksTypeAction = (tradingAccountsApiClient: TradingAccountsApiClient) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getTradingAccountBookType()
      dispatch(tradingAccountsSlice.actions.setBookType(data))
    } catch (e) {
      console.error('Cannot get book types.', e)
    }
  }
}

export const fetchServerAction = (
  platformTypeId: number,
  tradingAccountsApiClient: TradingAccountsApiClient
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const response = await tradingAccountsApiClient.getTradingAccountServerType(platformTypeId)
      dispatch(tradingAccountsSlice.actions.setServers(response))
    } catch (e) {
      console.error('Cannot get servers.', e)
    }
  }
}

export const fetchAllServerAction = (tradingAccountsApiClient: TradingAccountsApiClient) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const response = await tradingAccountsApiClient.getTradingAccountServerType()
      dispatch(tradingAccountsSlice.actions.setAllServers(response))
    } catch (e) {
      console.error('Cannot get servers.', e)
    }
  }
}

export const fetchTradingAccountStatusAction = (
  tradingAccountsApiClient: TradingAccountsApiClient
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getTradingAccountStatuses()
      dispatch(tradingAccountsSlice.actions.setTradingAccountStatus(data))
    } catch (e) {
      console.error('Cannot get trading account status.', e)
    }
  }
}

export const fetchTradingAccountTypeAction = (
  tradingAccountsApiClient: TradingAccountsApiClient,
  query?: PageQuery
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getTradingAccountType(query)
      dispatch(tradingAccountsSlice.actions.setTradingAccountType(data))
    } catch (e) {
      console.error('Cannot get trading account types.', e)
    }
  }
}

export const fetchExtendedTradingAccountTypeAction = (
  tradingAccountsApiClient: TradingAccountsApiClient,
  props: {
    platformTypeId: number
    currencyId: string
    accountGroupTypeId: number
  }
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getExtendedTradingAccountType(props)
      dispatch(tradingAccountsSlice.actions.setExtendedTradingAccountType(data))
    } catch (e) {
      console.error('Cannot get trading account types.', e)
    }
  }
}

export const fetchTradingAccountLeverageTypesForPlatformsAction = (
  clientId: string,
  tradingAccountsApiClient: TradingAccountsApiClient,
  platformTypeId: string | number
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const response = await tradingAccountsApiClient.getTradingAccountLeverageTypesForPlatforms(
        clientId,
        platformTypeId
      )
      dispatch(tradingAccountsSlice.actions.setTradingAccountLeverageTypesForPlatforms(response))
    } catch (e) {
      console.error('Cannot get client leverage type.', e)
    }
  }
}

export const fetchTradingAccountClientPlatformTypeAction = (
  tradingAccountsApiClient: TradingAccountsApiClient,
  clientId: string,
  tickmillProductId: number
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const response = await tradingAccountsApiClient.getTradingAccountClientPlatformType(
        clientId,
        tickmillProductId
      )
      dispatch(tradingAccountsSlice.actions.setTradingAccountPlatformType(response))
    } catch (e) {
      console.error('Cannot get client platform type.', e)
    }
  }
}

export const fetchTradingAccountClassificationTypeAction = (
  tradingAccountsApiClient: TradingAccountsApiClient
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getTradingAccountClassificationType()
      dispatch(tradingAccountsSlice.actions.setTradingAccountClassificationType(data))
    } catch (e) {
      console.error('Error during get account group classification types.', e)
    }
  }
}

export const fetchTradingAccountInstrumentTypeAction = (
  tradingAccountsApiClient: TradingAccountsApiClient
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getTradingAccountInstrumentTypes()
      dispatch(tradingAccountsSlice.actions.setTradingAccountInstrumentType(data))
    } catch (e) {
      console.error('Error during get trading account instrument types.', e)
    }
  }
}

export const fetchTradingAccountGroupTypeAction = (
  tradingAccountsApiClient: TradingAccountsApiClient
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getTradingAccountGroupType()
      dispatch(tradingAccountsSlice.actions.setTradingAccountGroupType(data))
    } catch (e) {
      console.error('Cannot get trading account group types.', e)
    }
  }
}

export const fetchTradingAccountGroupsAction = (
  tradingAccountsApiClient: TradingAccountsApiClient
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getTradingAccountGroupsAll()
      dispatch(tradingAccountsSlice.actions.setTradingAccountGroups(data))
    } catch (error) {
      console.error('Cannot get trading account groups.', error)
    }
  }
}

export const fetchLiveTradingAccountGroupsAction = (
  tradingAccountsApiClient: TradingAccountsApiClient
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getTradingAccountGroupsAll({
        AccountGroupTypeId: AccountGroupType.Live,
      })
      dispatch(tradingAccountsSlice.actions.setLiveTradingAccountGroups(data))
    } catch (error) {
      console.error('Cannot get trading account groups.', error)
    }
  }
}

export const fetchDemoTradingAccountGroupsAction = (
  tradingAccountsApiClient: TradingAccountsApiClient
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getTradingAccountGroupsAll({
        AccountGroupTypeId: AccountGroupType.Demo,
      })
      dispatch(tradingAccountsSlice.actions.setDemoTradingAccountGroups(data))
    } catch (error) {
      console.error('Cannot get trading account groups.', error)
    }
  }
}

export const fetchWelcomeTradingAccountGroupsAction = (
  tradingAccountsApiClient: TradingAccountsApiClient
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getTradingAccountGroupsAll({
        AccountGroupTypeId: AccountGroupType.Welcome,
      })
      dispatch(tradingAccountsSlice.actions.setWelcomeTradingAccountGroups(data))
    } catch (error) {
      console.error('Cannot get trading account groups.', error)
    }
  }
}

export const fetchTradingAccountGroupsByTradingAccountAction = (
  tradingAccountsApiClient: TradingAccountsApiClient,
  id: string
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getTradingAccountGroupsForTradingAccount(id)
      dispatch(tradingAccountsSlice.actions.setTradingAccountGroupsByTradingAccount(data))
    } catch (e) {
      console.error('Cannot get trading account groups.', e)
    }
  }
}

export const fetchAccountGroupByDemoAccount = (
  tradingAccountsApiClient: TradingAccountsApiClient,
  id: string
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getAccountGroupsByDemoAccount(id)
      dispatch(tradingAccountsSlice.actions.setAccountGroupsByDemoAccount(data))
    } catch (e) {
      console.error('Cannot get account groups by demo account.', e)
    }
  }
}

export const fetchAccountGroupByWelcomeAccount = (
  tradingAccountsApiClient: TradingAccountsApiClient,
  id: string
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getAccountGroupsByWelcomeAccount(id)
      dispatch(tradingAccountsSlice.actions.setAccountGroupsByWelcomeAccount(data))
    } catch (e) {
      console.error('Cannot get account groups by welcome account.', e)
    }
  }
}

export const getCurrenciesAction = (tradingAccountsApiClient: TradingAccountsApiClient) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getCurrencies()
      dispatch(tradingAccountsSlice.actions.setTradingAccountCurrencies(data))
    } catch (e) {
      console.error('Cannot get currencies.', e)
    }
  }
}

export const getCampaignTypesAction = (tradingAccountsApiClient: TradingAccountsApiClient) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getCampaignTypes()
      dispatch(tradingAccountsSlice.actions.setCampaignTypes(data))
    } catch (e) {
      console.error('Cannot get campaign types.', e)
    }
  }
}

export const getCampaignCalculationTypesAction = (
  tradingAccountsApiClient: TradingAccountsApiClient
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getCampaignCalculationTypes()
      dispatch(tradingAccountsSlice.actions.setCampaignCalculationTypes(data))
    } catch (e) {
      console.error('Cannot get campaign types.', e)
    }
  }
}

export const getCampaignStatusesAction = (tradingAccountsApiClient: TradingAccountsApiClient) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getCampaignStatuses()
      dispatch(tradingAccountsSlice.actions.setCampaignStatuses(data))
    } catch (e) {
      console.error('Cannot get campaign statuses.', e)
    }
  }
}

export const getCampaignIntervalTypesAction = (
  tradingAccountsApiClient: TradingAccountsApiClient
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getIntervalTypes()
      dispatch(tradingAccountsSlice.actions.setIntervalTypes(data))
    } catch (e) {
      console.error('Cannot get campaign interval types.', e)
    }
  }
}

export const getCampaignBusinessRuleTypesAction = (
  tradingAccountsApiClient: TradingAccountsApiClient
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getBusinessRuleTypes()
      dispatch(tradingAccountsSlice.actions.setCampaignBusinessRuleTypes(data))
    } catch (e) {
      console.error('Cannot get campaign business rule types.', e)
    }
  }
}

export const fetchTradingAccountSubscriptionClassificationTypesAction = (
  tradingAccountsApiClient: TradingAccountsApiClient
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getTradingAccountSubscriptionClassificationTypes()
      dispatch(tradingAccountsSlice.actions.setTradingAccountSubscriptionClassificationTypes(data))
    } catch (e) {
      console.error('Cannot get campaign interval types.', e)
    }
  }
}

export const fetchTradingAccountSubscriptionDetailTypesAction = (
  tradingAccountsApiClient: TradingAccountsApiClient
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getTradingAccountSubscriptionDetailTypes()
      dispatch(tradingAccountsSlice.actions.setTradingAccountSubscriptionDetailTypes(data))
    } catch (e) {
      console.error('Cannot get campaign interval types.', e)
    }
  }
}

export const getTradingAccountSubscriptionGroupsAction = (
  tradingAccountsApiClient: TradingAccountsApiClient
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getTradingAccountSubscriptionAllGroups()
      dispatch(tradingAccountsSlice.actions.setTradingAccountSubscriptionGroups(data))
    } catch (e) {
      console.error('Cannot get subscription groups.', e)
    }
  }
}

export const getTradingAccountSubscriptionBillingPeriodsAction = (
  tradingAccountsApiClient: TradingAccountsApiClient
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getTradingAccountSubscriptionBillingPeriods()
      dispatch(tradingAccountsSlice.actions.setTradingAccountSubscriptionBillingPeriods(data))
    } catch (e) {
      console.error('Cannot get subscription currencies.', e)
    }
  }
}

export const getDividendSubTypesAction = (tradingAccountsApiClient: TradingAccountsApiClient) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getDividendSubTypes()
      dispatch(tradingAccountsSlice.actions.setDividendSubTypes(data))
    } catch (e) {
      console.error('Cannot get dividend types.', e)
    }
  }
}

export const getCorporateActionTypes = (tradingAccountsApiClient: TradingAccountsApiClient) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getCorporateActionTypes()
      dispatch(tradingAccountsSlice.actions.setCorporateActionTypes(data))
    } catch (e) {
      console.error('Cannot get corporate action types.', e)
    }
  }
}

export const fetchOperatingSystemsAction = (tradingAccountsApiClient: TradingAccountsApiClient) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountsApiClient.getTradingAccountOperatingSystems()
      dispatch(tradingAccountsSlice.actions.setOperatingSystems(data))
    } catch (e) {
      console.error('Cannot get operating systems', e)
    }
  }
}

export const fetchRewardsCategories = (tradingAccountsApiClient: TradingAccountsApiClient) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const rewardCategories = await tradingAccountsApiClient.getRewardCategories()
      dispatch(tradingAccountsSlice.actions.setRewardCategories(rewardCategories))
    } catch (e) {
      console.error('Error during get reward categories', e)
    }
  }
}

export const fetchRewardTrackingMetrics = (tradingAccountsApiClient: TradingAccountsApiClient) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const rewardTrackingMetrics = await tradingAccountsApiClient.getRewardTrackingMetrics()
      dispatch(tradingAccountsSlice.actions.setRewardTrackingMetrics(rewardTrackingMetrics))
    } catch (e) {
      console.error('Error during get reward tracking metrics', e)
    }
  }
}

export const fetchRewardProgramStatuses = (tradingAccountsApiClient: TradingAccountsApiClient) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const rewardProgramStatuses = await tradingAccountsApiClient.getRewardProgramStatuses()
      dispatch(tradingAccountsSlice.actions.setRewardProgramStatuses(rewardProgramStatuses))
    } catch (e) {
      console.error('Error during get reward program statuses', e)
    }
  }
}

export const fetchRewardProgramTierConditionsLogic = (
  tradingAccountsApiClient: TradingAccountsApiClient
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const rewardProgramTierConditionsLogic =
        await tradingAccountsApiClient.getRewardProgramTierConditionsLogic()
      dispatch(
        tradingAccountsSlice.actions.setRewardProgramTierConditionsLogic(
          rewardProgramTierConditionsLogic
        )
      )
    } catch (e) {
      console.error('Error during get reward program tier logic', e)
    }
  }
}

export const fetchRewardProgramTierLogicalOperators = (
  tradingAccountsApiClient: TradingAccountsApiClient
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const rewardProgramTierLogicalOperators =
        await tradingAccountsApiClient.getRewardProgramTierLogicalOperators()
      dispatch(
        tradingAccountsSlice.actions.setRewardProgramTierLogicalOperators(
          rewardProgramTierLogicalOperators
        )
      )
    } catch (e) {
      console.error('Error during get reward program tier logic operator', e)
    }
  }
}

export const fetchRewardProgramResetCadence = (
  tradingAccountsApiClient: TradingAccountsApiClient
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const rewardProgramResetCadence =
        await tradingAccountsApiClient.getRewardProgramResetCadence()
      dispatch(tradingAccountsSlice.actions.setRewardProgramResetCadence(rewardProgramResetCadence))
    } catch (e) {
      console.error('Error during get reward program reset cadence', e)
    }
  }
}

export const fetchCountryOverrides = (
  tradingAccountsApiClient: TradingAccountsApiClient,
  query?: PageQuery
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const overrides: CountryOverridesResponse =
        await tradingAccountsApiClient.getCountryOverrides(query)
      dispatch(tradingAccountsSlice.actions.setCountryOverrides(overrides.items))
    } catch (error) {
      console.error('Error fetching country overrides', error)
    }
  }
}

export const fetchMarginBonusResultStatus = (tradingAccountApi: TradingAccountsApiClient) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const data = await tradingAccountApi.getMarginBonusResultStatuses()
      dispatch(tradingAccountsSlice.actions.setMarginBonusResultStatus(data))
    } catch (e) {
      console.error('Error fetching margin bonus result status', e)
    }
  }
}
